import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { OidcSecurityService, AuthOptions } from 'angular-auth-oidc-client';
import { Subject, from, takeUntil } from 'rxjs';

import { environment } from '../../../environments/environment';
import { resolveAuth0Enabled } from '../../auth/resolve-auth0-enabled';
import { DASHBOARD_ROUTE } from '../../shared/constants';
import { ReturnUrlStoreService } from '../../core/services/return-url-store.service';
import { buildLoginAuthOptions } from '../../core/utils/authorize-options';
import { LoginStep } from './login-step.enum';
import {
  SsoLookupStatus,
  SsoPreference,
  SsoProvider,
} from './sso-preference.model';
import { SsoPreferenceService } from './sso-preference.service';

// Re-exported so existing importers (and the template's type references) keep a
// single entry point; the enum itself now lives in login-step.enum.ts.
export { LoginStep } from './login-step.enum';

/**
 * Branded email-first login page (SYRC-6097, Option B).
 *
 * Flow, mirroring CorView's shipped implementation:
 *   1. User enters their email and continues.
 *   2. The page looks up their SSO preference and re-renders with the correct
 *      options — an SSO button when their company federates, plus the password
 *      path, or the password path alone.
 *   3. Continuing hands off to Auth0 with the resolved connection (and login_hint).
 *
 * The hand-off only supplies an Auth0 `connection` when the app is actually in
 * Auth0 mode (ConfigCat flag corinsights_auth0_enabled, resolved via
 * resolveAuth0Enabled()). Under the legacy CorView STS the connection param is
 * meaningless, so it is omitted and a plain authorize() is issued.
 *
 * Deep-link restore (SYRC-7252) is handled by ReturnUrlStoreService: an already
 * authenticated visitor is sent to their stored return URL, falling back to
 * /dashboard.
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly ssoPreferenceService = inject(SsoPreferenceService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly router = inject(Router);
  private readonly returnUrlStore = inject(ReturnUrlStoreService);
  // OnPush: async state changes (checkAuth, the SSO lookup, the flag resolve)
  // land outside a template event, so each one must markForCheck() to repaint.
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  /** Exposed for template comparisons. */
  readonly LoginStep = LoginStep;
  readonly SsoProvider = SsoProvider;

  readonly emailForm = this.formBuilder.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
  });

  step: LoginStep = LoginStep.EmailEntry;

  /** True while the SSO preference lookup is in flight. */
  isLookingUp = false;

  /**
   * True once a step-2 hand-off has started. The hand-off resolves the ConfigCat flag
   * asynchronously before redirecting, so without this the user could double-click and fire
   * two authorize() redirects with mismatched state/nonce.
   */
  isHandingOff = false;

  /** True while checking whether an existing session already covers this visit. */
  isHydrating = true;

  /**
   * Set when the email has no CorInsights account (lookup returned NotFound).
   * Distinct from `lookupFailed`.
   */
  emailNotFound = false;

  /**
   * Set when the lookup could not be completed (network, timeout, 5xx, bad shape).
   * The user is still allowed to continue — the lookup is an optimisation, not a gate.
   */
  lookupFailed = false;

  /** Resolved preference for the confirmed email, when the lookup succeeded. */
  preference: SsoPreference | null = null;

  /** The email the current `preference` was resolved for. */
  confirmedEmail = '';

  ngOnInit(): void {
    // Self-hydrate: someone who already has a live session and opens /login
    // directly (or in a second tab) belongs in the app, not on this form.
    this.oidcSecurityService
      .checkAuth()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ isAuthenticated }) => {
          if (isAuthenticated) {
            // Restore the deep link the user was originally headed to (SYRC-7252),
            // falling back to the dashboard.
            const returnUrl = this.returnUrlStore.read();
            this.returnUrlStore.clear();
            void this.router.navigateByUrl(returnUrl || `/${DASHBOARD_ROUTE}`);
            return;
          }
          // Not authenticated: the branded email-first page only applies in Auth0
          // mode. Gate the whole UI on corinsights_auth0_enabled so it is dark
          // until Auth0 is switched on. When the flag is off we reproduce the
          // legacy behaviour exactly — an immediate authorize() straight to the
          // CorView STS (the authority is resolved from the same flag by
          // auth-config.module.ts), so STS users never see this page.
          this.resolveLoginMode();
        },
        error: (err: unknown) => {
          // Log before recovering: swallowing this silently would hide every auth
          // failure (expired config, corrupt token, OIDC library error) from the
          // logs, leaving no trace of why sign-in fell back. Recovery is unchanged —
          // fall back to the flag-gated decision rather than stranding the user.
          console.error('[LoginComponent] checkAuth() failed.', err);
          this.resolveLoginMode();
        },
      });
  }

  /**
   * Decides, based on the ConfigCat flag corinsights_auth0_enabled, whether to
   * render the branded email-first page (Auth0 on) or short-circuit to a plain
   * authorize() against the legacy STS (Auth0 off).
   *
   * resolveAuth0Enabled() defaults to false on any timeout/error, so a flag
   * lookup problem can only ever fall back to the STS path — never block login.
   */
  private resolveLoginMode(): void {
    from(resolveAuth0Enabled())
      .pipe(takeUntil(this.destroy$))
      .subscribe((auth0Enabled) => {
        if (auth0Enabled) {
          // Auth0 mode — show the branded email-first form.
          this.isHydrating = false;
          this.cdr.markForCheck();
          return;
        }
        // Legacy STS mode — no branded page; go straight to the IdP as before.
        this.oidcSecurityService.authorize();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get emailControl() {
    return this.emailForm.controls.email;
  }

  /** True once the user has submitted an invalid email. */
  get showEmailValidationError(): boolean {
    return this.emailControl.invalid && this.emailControl.touched;
  }

  /**
   * True when an SSO option should be offered on the confirm step.
   *
   * Requires both ssoEnabled AND a routable provider. Without the provider check,
   * a response of sso_enabled: true with a null/absent sso_provider (which
   * normalizeSsoPreference maps to SsoProvider.None) would render an SSO button
   * whose hand-off resolves to no connection — byte-for-byte identical to the
   * plain "Continue to sign in" path, i.e. two buttons doing the same thing
   * (Richard — PR review). SsoProvider.Unknown still renders a generic button on
   * purpose: it reaches Auth0 with no connection hint, which is the intended
   * behaviour for an enabled-but-unrecognised provider.
   */
  get hasSsoOption(): boolean {
    return (
      this.preference?.ssoEnabled === true &&
      this.preference.provider !== SsoProvider.None
    );
  }

  /**
   * Label for the SSO button. Generic when the provider code was not recognised,
   * so an unexpected value never mislabels the destination.
   */
  get ssoButtonLabel(): string {
    switch (this.preference?.provider) {
      case SsoProvider.Microsoft:
        return 'Continue with Microsoft';
      case SsoProvider.Enterprise:
        return 'Continue with Enterprise SSO';
      default:
        return 'Continue with Single Sign-On';
    }
  }

  /** Step 1 submit — resolve the email's identity provider. */
  onEmailSubmit(): void {
    // Normalise in place BEFORE validating: Validators.email rejects surrounding
    // whitespace, so a pasted address would otherwise be reported as malformed.
    const rawEmail = this.emailControl.value;
    const email = rawEmail.trim();

    if (email !== rawEmail) {
      this.emailControl.setValue(email);
    }

    this.emailControl.markAsTouched();

    if (this.emailForm.invalid || this.isLookingUp) {
      return;
    }

    this.isLookingUp = true;
    this.emailNotFound = false;
    this.lookupFailed = false;

    this.ssoPreferenceService
      .getPreference(email)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isLookingUp = false;

        switch (result.status) {
          case SsoLookupStatus.Found:
            this.preference = result.preference;
            this.confirmedEmail = email;
            this.step = LoginStep.ConfirmIdentity;
            break;

          case SsoLookupStatus.NotFound:
            // Stay on step 1 so the user can correct the address.
            this.emailNotFound = true;
            break;

          case SsoLookupStatus.Error:
            // Advance anyway with no SSO options — the user must never be
            // hard-blocked by a failed lookup.
            this.lookupFailed = true;
            this.preference = null;
            this.confirmedEmail = email;
            this.step = LoginStep.ConfirmIdentity;
            break;
        }

        // OnPush: this fires after an async lookup, outside any template event.
        this.cdr.markForCheck();
      });
  }

  /** Step 2 — password path (no federated connection). */
  onContinueToSignIn(): void {
    this.handoffToIdentityProvider(null);
  }

  /** Step 2 — federated path, using the resolved SSO connection. */
  onContinueWithSso(): void {
    this.handoffToIdentityProvider(this.resolveSsoConnection());
  }

  /**
   * Resolves the Auth0 connection for the SSO hand-off.
   *
   * Enterprise/SAML customers carry their own connection in the SSO-preference response
   * (saml_connection_name). Microsoft/Azure AD does NOT — its connection is a tenant-wide Auth0
   * Enterprise Connection, so the response's connection is null and we fall back to the
   * environment's configured defaultSsoConnection. Verified against the cor-qa endpoint
   * (SYRC-6097): a Microsoft user returns sso_provider '1' with saml_connection_name: null.
   */
  private resolveSsoConnection(): string | null {
    const fromResponse = this.preference?.connectionName;
    if (fromResponse) {
      return fromResponse;
    }
    // The env default is the tenant-wide Microsoft/Azure AD Enterprise Connection,
    // so it is ONLY correct for Microsoft users. Applying it whenever connectionName
    // is empty would mis-route others: an Okta user (provider Enterprise,
    // saml_connection_name: null) — or an Unknown/None preference that still renders
    // a button — would be federated to the Microsoft IdP. Anyone non-Microsoft with a
    // missing connection goes to Auth0 with no connection hint instead (SYRC-6097 review).
    if (this.preference?.provider === SsoProvider.Microsoft) {
      return environment.auth0Config?.defaultSsoConnection ?? null;
    }
    return null;
  }

  /** Return to step 1 to correct the email address. */
  onChangeEmail(): void {
    this.step = LoginStep.EmailEntry;
    this.preference = null;
    this.confirmedEmail = '';
    this.emailNotFound = false;
    this.lookupFailed = false;
  }

  /** Clear the not-found message as soon as the user edits the address. */
  onEmailInput(): void {
    if (this.emailNotFound) {
      this.emailNotFound = false;
    }
  }

  /**
   * Hands off to the identity provider via oidcSecurityService.authorize().
   *
   * `login_hint` is always passed so the IdP can pre-fill / pre-select the account. The Auth0-only
   * params — `prompt=login` and the `connection` hint — are only meaningful when the app is in
   * Auth0 mode (ConfigCat flag corinsights_auth0_enabled); under the legacy CorView STS they are
   * omitted. See buildLoginAuthOptions() for why `prompt=login` is sent (SYRC-7399).
   */
  private handoffToIdentityProvider(connection: string | null): void {
    // Guard against a double-click firing two redirects while the flag resolves.
    if (this.isHandingOff) {
      return;
    }
    this.isHandingOff = true;

    from(resolveAuth0Enabled())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (auth0Enabled) => {
          // login_hint always; in Auth0 mode also prompt=login (a fresh sign-in, so Auth0's per-user
          // MFA decision always runs — SYRC-7399, matching corviewng's LoginService.authorizeViaOidc)
          // and the SSO connection when there is one. See authorize-options.ts.
          const authOptions: AuthOptions = buildLoginAuthOptions(
            this.confirmedEmail,
            auth0Enabled,
            connection,
          );
          try {
            this.oidcSecurityService.authorize(undefined, authOptions);
            // On success authorize() navigates the whole browser away, so control
            // never returns here — isHandingOff staying true is fine in that case.
          } catch (err) {
            // authorize() failed to redirect. Without resetting the guard, BOTH
            // sign-in buttons would stay disabled forever with no way to retry
            // short of a full page reload. Re-enable so the user can try again.
            this.resetHandoff(err);
          }
        },
        error: (err: unknown) => {
          // The flag resolve itself failed before we could even call authorize().
          // Same recovery: clear the guard so the buttons are usable again.
          this.resetHandoff(err);
        },
      });
  }

  /** Re-enable the sign-in buttons after a hand-off that did not navigate away. */
  private resetHandoff(err: unknown): void {
    console.error('[LoginComponent] Sign-in hand-off failed to redirect.', err);
    this.isHandingOff = false;
    this.cdr.markForCheck();
  }
}
